import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getOrderStatus } from "@/lib/api";
import { useT } from "@/context/LanguageContext";
import { useSEO } from "@/hooks/useSEO";

const STATUS_LABELS_KEY = {
  paid:       "order.status.paid",
  preparing:  "order.status.preparing",
  shipped:    "order.status.shipped",
  delivered:  "order.status.delivered",
  refunded:   "order.status.refunded",
  cancelled:  "order.status.cancelled",
};

const STEPS = ["paid", "preparing", "shipped", "delivered"];

const money = (n, cur = "EUR") =>
  new Intl.NumberFormat("en-EU", { style: "currency", currency: cur }).format(Number(n || 0));

const StepBar = ({ current }) => {
  const currentIdx = STEPS.indexOf(current);
  if (currentIdx === -1) return null;
  return (
    <div className="grid grid-cols-4 gap-1 mt-6" data-testid="order-step-bar">
      {STEPS.map((s, i) => (
        <div key={s} className="flex flex-col items-center gap-2">
          <div
            className={`h-1 w-full rounded-full ${i <= currentIdx ? "bg-[var(--gl-orange)]" : "bg-[#F1DCC5]"}`}
          />
          <div className={`font-badge text-[10px] tracking-[0.2em] uppercase ${i <= currentIdx ? "text-[#241C18]" : "text-[#8A7568]"}`}>
            {s}
          </div>
        </div>
      ))}
    </div>
  );
};

const OrderStatus = () => {
  const { t } = useT();
  useSEO({ noindex: true });
  const { sessionId } = useParams();
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const data = await getOrderStatus(sessionId);
        setOrder(data);
        if (data && !data.pending) {
          setStatus("loaded");
          return;
        }
        if (attempts >= 8) {
          setStatus("pending");
          return;
        }
        setTimeout(tick, 2500);
      } catch (e) {
        // 404 = session doesn't exist. No point retrying — fail fast.
        if (e?.response?.status === 404) {
          setStatus("failed");
          return;
        }
        if (attempts >= 4) setStatus("failed");
        else setTimeout(tick, 2500);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  if (status === "loading") {
    return (
      <div className="min-h-screen pt-32 px-6 md:px-12 pb-24 max-w-3xl mx-auto text-center">
        <div className="font-badge text-[10px] tracking-[0.28em] text-[#8A7568]">{t("order.loading")}</div>
        <div className="font-serif-display text-4xl md:text-5xl mt-3 text-[#241C18]">…</div>
      </div>
    );
  }

  if (status === "failed" || !order) {
    return (
      <div className="min-h-screen pt-32 px-6 md:px-12 pb-24 max-w-3xl mx-auto text-center">
        <h1 className="font-serif-display text-4xl md:text-5xl">{t("order.notFound.title")}</h1>
        <p className="mt-4 text-[#8A7568]">{t("order.notFound.body")}</p>
        <Link to="/" className="btn-orange mt-10 inline-block">{t("order.notFound.back")}</Link>
      </div>
    );
  }

  if (status === "pending" || order.pending) {
    return (
      <div className="min-h-screen pt-32 px-6 md:px-12 pb-24 max-w-3xl mx-auto text-center">
        <div className="font-badge text-[10px] tracking-[0.28em] text-[#8A7568]">{t("order.eyebrow.pending")}</div>
        <h1 className="font-serif-display text-4xl md:text-5xl mt-3">{t("order.pending.title")}</h1>
        <p className="mt-4 text-[#8A7568] max-w-md mx-auto">{t("order.pending.body")}</p>
      </div>
    );
  }

  const addr = order.shipping_address || {};
  return (
    <div className="min-h-screen pt-24 md:pt-32 px-6 md:px-12 pb-24 max-w-3xl mx-auto" data-testid="order-status-page">
      <div className="font-badge text-[10px] tracking-[0.28em] text-[var(--gl-orange-hover)]">
        {t("order.eyebrow")}
      </div>
      <h1 data-testid="order-number" className="font-serif-display text-4xl md:text-6xl mt-2 tracking-tight text-[#241C18]">
        {order.order_number}
      </h1>
      <p className="mt-3 text-[#8A7568] font-badge text-[11px] tracking-[0.2em] uppercase">
        {t(STATUS_LABELS_KEY[order.status] || "order.status.paid")}
        {order.paid_at ? ` · ${new Date(order.paid_at).toLocaleDateString()}` : ""}
      </p>

      <StepBar current={order.status} />

      {order.tracking_number && (
        <div className="mt-8 p-5 bg-white border border-[#F1DCC5] rounded-2xl">
          <div className="font-badge text-[10px] tracking-[0.24em] text-[#8A7568]">
            {t("order.trackingNumber")}
          </div>
          <a
            href={order.tracking_url || `https://www.google.com/search?q=${order.tracking_number}`}
            target="_blank"
            rel="noreferrer"
            data-testid="order-tracking-link"
            className="font-serif-display text-2xl mt-1 block text-[#241C18] underline underline-offset-4 hover:text-[var(--gl-orange-hover)]"
          >
            {order.tracking_number}
          </a>
        </div>
      )}

      <div className="mt-10">
        <div className="font-badge text-[10px] tracking-[0.24em] text-[#8A7568] mb-4">
          {t("order.items")}
        </div>
        <div className="space-y-3">
          {(order.items || []).map((it, i) => (
            <div key={i} className="flex items-center gap-4 py-3 border-b border-[#F1DCC5] last:border-0">
              {it.image && <img src={it.image} alt="" className="w-16 h-20 object-cover" />}
              <div className="flex-1 min-w-0">
                <div className="font-serif-display text-lg text-[#241C18] truncate">{it.name}</div>
                <div className="text-xs text-[#8A7568] mt-1">{it.variant_name || ""} · Qty {it.quantity}</div>
              </div>
              <div className="text-sm text-[#241C18]">{money(it.unit_price * it.quantity, order.currency)}</div>
            </div>
          ))}
        </div>
        <div className="mt-6 space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-[#8A7568]">{t("order.subtotal")}</span><span>{money(order.subtotal, order.currency)}</span></div>
          <div className="flex justify-between"><span className="text-[#8A7568]">{t("order.shipping")}</span><span>{money(order.shipping_amount, order.currency)}</span></div>
          <div className="flex justify-between text-base font-semibold pt-2 border-t border-[#241C18] mt-2">
            <span>{t("order.total")}</span>
            <span>{money(order.total, order.currency)}</span>
          </div>
        </div>
      </div>

      {addr.line1 && (
        <div className="mt-10">
          <div className="font-badge text-[10px] tracking-[0.24em] text-[#8A7568] mb-3">
            {t("order.shipTo")}
          </div>
          <div className="text-sm text-[#241C18] leading-relaxed">
            <div>{addr.name}</div>
            <div>{addr.line1}</div>
            {addr.line2 && <div>{addr.line2}</div>}
            <div>{[addr.postal_code, addr.city].filter(Boolean).join(" ")}</div>
            <div>{addr.country}</div>
          </div>
        </div>
      )}

      <div className="mt-12 flex gap-3">
        <Link to="/collection/all" className="btn-orange">{t("order.continue")}</Link>
      </div>
    </div>
  );
};

export default OrderStatus;
